import { StopTimesDiagram, SyncFlowDiagram } from "@/components/collections/IntegrationsDiagrams"
import { ReferenceCard, ReferenceSection } from "@/components/collections/ReferenceCard"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { TRACKPOD_PLAUSIBILITY_WINDOW_HOURS } from "@/lib/stopTiming"
import { SYNC_DOWN_MINUTES, SYNC_STALE_MINUTES } from "@/lib/syncFreshness"
import { cn } from "@/lib/utils"

/**
 * Where monitoring times come from. Geofence radii, poll cadence and the auto-cancel hour have no
 * constant here; check them by hand against collections-samsara-sync and collections-trackpod-sync.
 */

function Verdict({ wins, flagged }: { wins: "samsara" | "driver" | "plan"; flagged?: boolean }) {
  const pill = "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium"
  const source = {
    samsara: { label: "Samsara", className: "bg-palette-blueGreen100 text-palette-blueGreen900" },
    driver: { label: "Driver", className: "bg-palette-honeycomb100 text-palette-honeycomb900" },
    plan: {
      label: "Plan only",
      className: "bg-palette-jungleGreen100 text-palette-jungleGreen700",
    },
  }[wins]

  return (
    <div className="mb-1.5 flex flex-wrap items-center gap-1">
      <span className={cn(pill, source.className)}>{source.label}</span>
      {flagged && (
        <span className={cn(pill, "bg-variance-late-tint text-variance-late-strip")}>Flagged</span>
      )}
    </div>
  )
}

export function IntegrationsReference() {
  return (
    <ReferenceCard
      title="Integrations"
      intro="How Samsara and Track-POD data reaches the monitoring screens, what happens when the two disagree, and how current any of it is"
    >
      <ReferenceSection
        title="Where the times come from"
        intro="Two systems report every stop, and neither is complete on its own. Which of them you are looking at is worth knowing, because they fail in opposite directions."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">System</TableHead>
              <TableHead className="w-56">Reports</TableHead>
              <TableHead>How, and how it fails</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Samsara</TableCell>
              <TableCell>
                Vehicle position, route start, arrival and departure at each stop, live ETAs, stop
                state
              </TableCell>
              <TableCell>
                From the vehicle&rsquo;s GPS crossing a geofence — 50 m around a saved address, 100
                m around a one-off location. Accurate when it fires and{" "}
                <strong>silent when it does not</strong>: a vehicle parked round the corner, a cold
                GPS fix, or an address geocoded to the wrong side of a building all produce a stop
                with no arrival at all, or one Samsara calls skipped.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Track-POD</TableCell>
              <TableCell>Arrival, departure and a per-order status</TableCell>
              <TableCell>
                Typed by a driver on a phone, so it is inconsistent between drivers and occasionally
                nonsense — but its status is the only signal that separates &ldquo;we skipped
                this&rdquo; from &ldquo;we collected here and the GPS never noticed&rdquo;. Also the
                reason times arrive late: drivers routinely close orders at the end of a shift, or
                the next morning.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <SyncFlowDiagram />
        <p className="text-xs text-palette-jungleGreen500">
          Read left to right. Each stored column has exactly one writer — Track-POD physically
          cannot overwrite an arrival Samsara recorded — and the two are only brought together on
          the way out to a screen. That is why a correction to the rules is a code change and never
          a migration, and why a disagreement stays visible instead of being quietly reconciled
          away.
        </p>
      </ReferenceSection>

      <ReferenceSection
        title="How the two are combined"
        intro="Resolved every time a monitoring screen reads a stop, never written back — so each underlying column keeps exactly one writer and a correction can be changed without a migration. The rule is asymmetric, and the asymmetry is the whole design: Samsara wins wherever it has an opinion, and Track-POD fills its silences."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-72">When…</TableHead>
              <TableHead>Monitoring shows</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Samsara has a time</TableCell>
              <TableCell>
                <Verdict wins="samsara" />
                Samsara&rsquo;s, always — however confident Track-POD is. Manual entry does not get
                to overwrite instrumentation.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">
                Samsara has nothing, the driver recorded a plausible time
              </TableCell>
              <TableCell>
                <Verdict wins="driver" />
                The driver&rsquo;s time, marked with a small phone icon. A blank here reads as
                &ldquo;running late&rdquo;, which is the wrong answer, and a driver-entered time is
                worth less trust than a GPS one — so it is shown, and labelled.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">
                Samsara says skipped, the driver says collected
              </TableCell>
              <TableCell>
                <Verdict wins="driver" flagged />
                <strong>Completed</strong>, flagged as a disagreement. This is the missed geofence
                the whole arrangement exists for: an absent geofence is an absence of evidence, and
                a driver marking a collection is evidence.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">
                Samsara still says scheduled or en route, the driver says collected
              </TableCell>
              <TableCell>
                <Verdict wins="driver" flagged />
                The same miss, one step earlier. Completed and flagged, but only once the
                stop&rsquo;s planned departure has passed or the driver logged an arrival — so a
                stop still in progress is never closed early by a driver tapping ahead of
                themselves.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">
                Samsara says completed, the driver recorded nothing collected
              </TableCell>
              <TableCell>
                <Verdict wins="samsara" flagged />
                Stays completed — it is not in doubt that the vehicle was there — but flagged. An
                attended stop with nothing to show for it is a real question for the depot.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Neither has anything</TableCell>
              <TableCell>
                <Verdict wins="plan" />
                The planned times, as planned. Nothing is inferred.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <p className="text-xs text-palette-jungleGreen500">
          A driver-entered time more than {TRACKPOD_PLAUSIBILITY_WINDOW_HOURS} hours from its plan
          is discarded, as is a departure recorded before its own arrival. Orders are matched back
          to stops on a reference we send with each one; where an older route lacks it, the fallback
          is the order name, and that is abandoned rather than guessed at when a route visits the
          same site twice — a missing time is recoverable, a time written onto the wrong stop is
          not.
        </p>
      </ReferenceSection>

      <ReferenceSection
        title="Measured, and merely derived"
        intro="Two of the four times on a stop are records of something that happened. The other two are arithmetic, and the difference matters when you are deciding whether to act on one."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">Figure</TableHead>
              <TableHead>What it actually is</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Actual arrival / departure</TableCell>
              <TableCell>
                Measured. A geofence crossing, or a driver&rsquo;s entry where the geofence was
                silent.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Estimated arrival (ETA)</TableCell>
              <TableCell>
                Samsara&rsquo;s own prediction from the vehicle&rsquo;s position, refreshed on every
                poll for as long as the stop is still ahead of the vehicle.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Estimated departure</TableCell>
              <TableCell>
                Never measured, and not really Samsara&rsquo;s either: the <em>arrival ETA</em> plus
                the planned service window, calculated by the sync each time an ETA arrives. Read it
                as &ldquo;if we get there when we think, and the stop takes as long as
                planned&rdquo; — not as a prediction of the departure itself.{" "}
                <strong>It is only recalculated while a new arrival ETA is still coming in.</strong>{" "}
                Samsara has nothing left to predict once the vehicle is on site, so from that point
                the figure is frozen on a guess made before it got there, and a stop overrunning its
                service window will show a departure time already in the past. Treat a departure
                estimate on a stop the driver has arrived at as the plan, not as an expectation.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Time on site</TableCell>
              <TableCell>
                Actual departure minus actual arrival, so it only exists once a stop is finished.
                There is no live equivalent: while the vehicle is still there, the arrival time and
                the clock are the only honest inputs.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <StopTimesDiagram />
        <p className="text-xs text-palette-jungleGreen500">
          A worked example of the row above. The predicted bar is the same length as the planned one
          because that is all it ever was — the arrival prediction plus the planned service window,
          slid along. Once the vehicle arrived at 10:18 there was nothing left for Samsara to
          predict, so the 10:42 estimate stopped moving; at 10:55 the board is still showing it, and
          the honest reading of the stop is the arrival time and the clock beside it.
        </p>
      </ReferenceSection>

      <ReferenceSection
        title="What runs, and when"
        intro="Everything on these screens is a snapshot of the last successful run. The readout in the band above the timeline reports the older of the two integrations, because the worse one is what decides whether the board is right."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">Job</TableHead>
              <TableHead className="w-32">Runs</TableHead>
              <TableHead>Does</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Samsara poll</TableCell>
              <TableCell>Every 5 minutes</TableCell>
              <TableCell>
                Pulls vehicle positions, ETAs, arrivals, departures and stop states for the
                day&rsquo;s routes; recalculates each route&rsquo;s delay and status. Any run in the
                07:00 hour also cancels the previous day&rsquo;s routes that never started.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Track-POD ingest</TableCell>
              <TableCell>Every 5 minutes</TableCell>
              <TableCell>
                Pulls today&rsquo;s orders back from Track-POD and updates the driver-entered
                columns. It cannot touch the Samsara ones — the set of columns it may write is fixed
                in one place and asserted in tests, because two syncs quietly fighting over one
                column is the failure this design exists to rule out.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Track-POD reconcile</TableCell>
              <TableCell>By hand, for one past day</TableCell>
              <TableCell>
                Re-pulls a whole day and reports where the two systems now disagree, split into
                missed geofences, attended-but-empty stops, and stops simply still in progress. The
                routine ingest has no memory and never revisits a day, so a stop that looked skipped
                all afternoon and got its driver record at 19:00 is only picked up here.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Route push</TableCell>
              <TableCell>On create and on edit</TableCell>
              <TableCell>
                The other direction: a new or changed route is pushed out to both systems, and a
                cancelled one is withdrawn. Everything above depends on it — a stop Samsara was
                never told about has no geofence to cross.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <p className="text-xs text-palette-jungleGreen500">
          Pre-cutover, the schedules are not yet installed and both polls are triggered by hand: the
          old system and this one push to the same Samsara and Track-POD accounts, and running both
          sets of jobs at once would double-push every route. Until then, a &ldquo;last
          synced&rdquo; age of hours is expected rather than a fault.
        </p>
      </ReferenceSection>
      <ReferenceSection
        title="When to stop trusting it"
        intro="Three numbers govern how much of the above is believed. All are read from the code that enforces them, so changing one changes this page with it."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-32">Value</TableHead>
              <TableHead>What it governs</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-semibold tabular-nums">{SYNC_STALE_MINUTES} min</TableCell>
              <TableCell>
                How old the last successful sync can get before the &ldquo;synced&rdquo; readout
                above the timeline turns amber. Roughly three missed five-minute ticks — one miss is
                unremarkable and must not raise an alarm.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-semibold tabular-nums">{SYNC_DOWN_MINUTES} min</TableCell>
              <TableCell>
                When the sync is treated as down and the readout turns red. Long enough that a slow
                run or a brief API outage has had every chance to recover on its own. Everything on
                the monitoring screens is only as current as this number.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-semibold tabular-nums">
                {TRACKPOD_PLAUSIBILITY_WINDOW_HOURS} h
              </TableCell>
              <TableCell>
                How far from its planned time a driver-entered Track-POD time may fall before it is
                discarded. Generous on purpose: a genuinely late collection is worth recording, and
                the errors being caught — yesterday&rsquo;s stops closed this morning, a mis-set
                date, a timezone mishandled between the phone and the API — land days out rather
                than hours.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </ReferenceSection>
    </ReferenceCard>
  )
}

export default IntegrationsReference
